/**
 * LAT-885 frozen deprecation data — the eight hand-authored `*.usage` tabs and the
 * twenty-seven `mentions` source pairs they produced.
 *
 * These lists are literals on purpose. The framework config that defined the tabs and
 * their template fields was deleted in the same change, so at the time the retirement
 * migrations run there is nothing left to derive them from. They are frozen history:
 * never extend them for a tab retired later, write that retirement its own list.
 *
 * Pair scoping is load-bearing. Four usage field ids are reused across entity types —
 * `usedByDatasets` (sourceSystem, dataset, mapping), `usedByMetrics` (dataset, mapping),
 * `usedBySemanticModels` (dataset, metricLogic, mapping) and `usedByConsumptionEntities`
 * (dataset, metricLogic) — while within a single entity type each id belonged to exactly
 * one usage tab. Matching on `sourceFieldId` alone would therefore retire rows from
 * entity types this change never touched; matching on `${sourceType}::${sourceFieldId}`
 * retires exactly the authored usage sections.
 *
 * Caveat inherited from the ledger: `mentions.sourceFieldId` is a bare section id, not a
 * tab-qualified one (see `loadTemplateFieldLabels` in `packages/convex/convex/lib/
 * entityUsage.ts`). Should an entity type ever reuse one of these ids on a surviving tab,
 * the pair can no longer tell the two apart — verified not to be the case for these 27.
 */
export const RETIRED_USAGE_TAB_KEYS = new Set([
    "sourceSystem.usage",
    "dataset.usage",
    "field.usage",
    "metricLogic.usage",
    "mapping.usage",
    "hierarchy.usage",
    "timeLogic.usage",
    "semanticModel.usage",
]);
/** `${sourceType}::${sourceFieldId}` for every field the retired usage tabs authored. */
export const RETIRED_USAGE_MENTION_SOURCE_PAIRS = new Set([
    "sourceSystem::usedByDatasets",
    "sourceSystem::usedByMappings",
    "sourceSystem::indirectDownstreamUsage",
    "dataset::usedByDatasets",
    "dataset::usedByMetrics",
    "dataset::usedBySemanticModels",
    "dataset::usedByConsumptionEntities",
    "field::keysOrJoins",
    "field::usedInMappings",
    "field::usedInMetrics",
    "field::usedInFiltersVisualsOrHierarchies",
    "metricLogic::usedByKpis",
    "metricLogic::usedByOtherMetrics",
    "metricLogic::usedBySemanticModels",
    "metricLogic::usedByConsumptionEntities",
    "mapping::usedByDatasets",
    "mapping::usedByMetrics",
    "mapping::usedBySemanticModels",
    "hierarchy::usedInMetricsAndKpis",
    "hierarchy::usedInSemanticModels",
    "hierarchy::usedInDashboardsAndFilters",
    "timeLogic::datasetsAffected",
    "timeLogic::metricsAndKpisAffected",
    "timeLogic::consumptionEffects",
    "semanticModel::dashboardConsumers",
    "semanticModel::agentConsumers",
    "semanticModel::kpiCoverage",
]);
